import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import './Conta.css';

// --- IMPORTAÇÃO DAS LOGOS DOS APPS ---
import logoCalcLeite from "../../assets/calcleite.png";
import logoCalcCafe from "../../assets/calccafe.png";
import logoCalcBov from "../../assets/calcbov.png";
import logoCalcAgro from "../../assets/calcagro.png";

const appDetails = {
  calcleite: { name: 'CalcLeite', logo: logoCalcLeite },
  calccafe:  { name: 'CalcCafé',  logo: logoCalcCafe },
  calcbov:   { name: 'CalcBov',   logo: logoCalcBov },
  calcagro:  { name: 'CalcAgro',  logo: logoCalcAgro },
};

const Conta = () => {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();

  const [isSyncing, setIsSyncing] = useState(false);
  const [actionLoading, setActionLoading] = useState({ type: null, appKey: null });

  useEffect(() => {
    const syncSubscription = async () => {
      if (!user) return;
      setIsSyncing(true);
      try {
        const token = await user.getIdToken(true);
        const response = await fetch("https://backendcustosagro-e51a.onrender.com/sync-subscription", {
          method: "POST",
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ uid: user.uid }),
        });
        if (!response.ok) throw new Error("Falha ao sincronizar dados do servidor.");
      } catch (error) {
        console.error("Erro ao sincronizar assinatura:", error);
      } finally {
        setIsSyncing(false);
      }
    };
    syncSubscription();
  }, [user]);

  const handleCancelSubscription = async (appKey) => {
    if (!user) return;
    setActionLoading({ type: 'cancel', appKey });
    try {
      const token = await user.getIdToken(true);
      const response = await fetch("https://backendcustosagro-e51a.onrender.com/cancel-subscription", {
        method: "POST",
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ uid: user.uid, app: appKey }),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error || "Falha ao cancelar assinatura");
      // Atualizar localmente ou esperar Firestore atualizar via useAuth
      alert("Assinatura marcada para cancelamento.");
    } catch (error) {
      console.error("Erro ao cancelar assinatura:", error);
      alert("Erro: " + error.message);
    } finally {
      setActionLoading({ type: null, appKey: null });
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setActionLoading({ type: 'delete', appKey: null });
    try {
      const token = await user.getIdToken(true);
      const response = await fetch("https://backendcustosagro-e51a.onrender.com/delete-account", {
        method: "POST",
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error || "Não foi possível excluir a conta.");
      alert("Conta excluída com sucesso.");
      navigate('/login');
    } catch (error) {
      console.error("Erro ao excluir conta:", error);
      alert("Erro: " + error.message);
    } finally {
      setActionLoading({ type: null, appKey: null });
    }
  };

  const toDate = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000);
    }
    if (typeof timestamp.toDate === "function") {
      return timestamp.toDate();
    }
    const date = new Date(timestamp);
    return isNaN(date) ? null : date;
  };

  const calcularDiasRestantes = (dataTermino) => {
    if (!dataTermino) return "N/A";
    const termino = new Date(dataTermino);
    if (isNaN(termino)) return "N/A";
    const hoje = new Date();
    const diffTime = Math.max(0, termino.getTime() - hoje.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)).toString();
  };

  const subscribedApps = useMemo(() => 
    userData ? Object.keys(appDetails).filter(appKey => userData[appKey] === true) : [], 
    [userData]
  );

  const getDataTermino = (sub) => {
    const trialEnd = sub.trialEnd ? new Date(sub.trialEnd.seconds * 1000) : null;
    const periodEnd = sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd.seconds * 1000) : null;
    const lastPayment = sub.lastPayment ? new Date(sub.lastPayment.seconds * 1000) : null;
    if (sub.status === "trialing" && trialEnd) return trialEnd;
    if (sub.status === "active" && periodEnd) return periodEnd;
    if (sub.status === "active" && lastPayment) {
      const estimatedEnd = new Date(lastPayment);
      estimatedEnd.setDate(estimatedEnd.getDate() + 30);
      return estimatedEnd;
    }
    return null;
  };

  if (loading) return <div className="loading">Carregando dados da conta...</div>;
  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="conta-container">
      <h1>Gerenciar Conta</h1>
      <div className="conta-card">
        <div className="card-header">
          <h2>Minhas Assinaturas</h2>
          <button onClick={() => window.location.reload()} className="sync-button" disabled={isSyncing}>
            {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        </div>

        {subscribedApps.length > 0 ? (
          <div className="subscription-cards">
            {subscribedApps.map(appKey => {
              const sub = userData[`subscriptions.${appKey}`];
              if (!sub) return null;

              const willCancelField = `willCancelAtPeriodEnd${appKey.charAt(0).toUpperCase() + appKey.slice(1)}`;
              const willCancel = userData?.[willCancelField] || false;

              let statusText = "Inativo";
              if (willCancel) {
                statusText = "Cancelamento agendado";
              } else if (sub.status === 'trialing') {
                statusText = "Em teste";
              } else if (sub.status === 'active') {
                statusText = "Ativo";
              }

              const dataTermino = getDataTermino(sub);
              const diasRestantes = calcularDiasRestantes(dataTermino);
              const dataFormatada = dataTermino
                ? dataTermino.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })
                : "N/A";

              return (
                <div className="subscription-card" key={appKey}>
                  <div className="subscription-card-header">
                    <img 
                      src={appDetails[appKey]?.logo} 
                      alt={`Logo ${appDetails[appKey]?.name}`} 
                      className="subscription-card-logo"
                    />
                    <h3>{appDetails[appKey]?.name || appKey}</h3>
                  </div>

                  <p><strong>Plano:</strong> {sub.plan || "N/A"}</p>
                  <p><strong>Status:</strong> <span className={`status-${statusText.toLowerCase().replace(/ /g, '-')}`}>{statusText}</span></p>
                  {dataTermino && diasRestantes !== "N/A" && diasRestantes !== "0" && (
                    <p><strong>Dias restantes:</strong> {diasRestantes} dias</p>
                  )}
                  {dataFormatada !== "N/A" && (
                    <p>
                      <strong>
                        {willCancel ? "Assinatura será cancelada em:" : "Próxima data de cobrança:"}
                      </strong> {dataFormatada}
                    </p>
                  )}
                  {willCancel ? (
                    <p className="cancelled-message">Sua assinatura não será renovada.</p>
                  ) : (
                    <button 
                      className="action-button cancel-button" 
                      onClick={() => handleCancelSubscription(appKey)}
                      disabled={actionLoading.type === 'cancel' && actionLoading.appKey === appKey}
                    >
                      {actionLoading.type === 'cancel' && actionLoading.appKey === appKey ? 'Cancelando...' : 'Cancelar Assinatura'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p>Você ainda não possui assinaturas ativas. <Link to="/assinar">Assine um de nossos apps!</Link></p>
        )}
      </div>

      <div className="conta-card danger-zone">
        <h2>Zona de Perigo</h2>
        <p>Esta ação não pode ser desfeita. Isso excluirá permanentemente sua conta, seus dados e cancelará todas as suas assinaturas ativas.</p>
        <button 
          className="action-button delete-button" 
          onClick={handleDeleteAccount}
          disabled={actionLoading.type === 'delete'}
        >
          {actionLoading.type === 'delete' ? 'Excluindo...' : 'Excluir Minha Conta'}
        </button>
      </div>
    </div>
  );
};

export default Conta;